import { ref, computed, onBeforeUnmount } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { TrpcClient } from "@app/vue/providers/TrpcProvider";

export type ExportPresentation = "pdf" | "xlsx";
export type ExportStatus = "starting" | "running" | "done" | "error";

/** One tracked export. Lives at the page level so it survives tab switches. */
export type ExportRun = {
  /** Local id, assigned before the server run exists. */
  id: string;
  tabId: string;
  definitionId: string;
  presentation: ExportPresentation;
  parameters: Record<string, unknown>;
  runId: string | null;
  stage: string;
  rows: number;
  status: ExportStatus;
  error: string | null;
};

const POLL_MS = 1500;
const TERMINAL_OK = "complete";
const TERMINAL_BAD = new Set(["errored", "terminated", "unknown"]);

function base64ToBlob(base64: string, contentType: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: contentType });
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Page-level export manager. Tracks one active export per tab (a new export
 * supersedes the previous one for that tab) while still allowing different tabs
 * to export concurrently. Completed exports are NOT auto-downloaded — the user
 * clicks the button to download when ready.
 */
export function useDataRoomExports(trpc: TrpcClient) {
  const runs = ref<ExportRun[]>([]);
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const { locale } = useLocalization();

  const patch = (id: string, updates: Partial<ExportRun>) => {
    const run = runs.value.find((r) => r.id === id);
    if (run) Object.assign(run, updates);
  };

  const stopTimer = (id: string) => {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
  };

  const removeRun = (id: string) => {
    stopTimer(id);
    const idx = runs.value.findIndex((r) => r.id === id);
    if (idx >= 0) runs.value.splice(idx, 1);
  };

  /** Clear (and cancel any in-flight) runs belonging to a tab. */
  const resetTab = (tabId: string) => {
    for (const run of runs.value.filter((r) => r.tabId === tabId)) {
      if (
        run.runId &&
        (run.status === "starting" || run.status === "running")
      ) {
        trpc.dataroom.cancelRun.mutate({ runId: run.runId }).catch(() => {});
      }
      removeRun(run.id);
    }
  };

  const poll = async (id: string) => {
    const run = runs.value.find((r) => r.id === id);
    if (!run || !run.runId) return;
    try {
      const res = await trpc.dataroom.getRun.query({ runId: run.runId });
      if (res.progress) {
        patch(id, {
          stage: res.progress.stage,
          rows: res.progress.rowsExtracted ?? 0,
        });
      }
      if (res.workflowStatus === TERMINAL_OK) {
        patch(id, { status: "done", stage: "completed" });
        stopTimer(id);
        return;
      }
      if (TERMINAL_BAD.has(res.workflowStatus)) {
        patch(id, { status: "error", error: "export_failed" });
        stopTimer(id);
        return;
      }
      patch(id, { status: "running" });
      timers.set(
        id,
        setTimeout(() => poll(id), POLL_MS),
      );
    } catch (e) {
      patch(id, {
        status: "error",
        error: e instanceof Error ? e.message : "export_failed",
      });
      stopTimer(id);
    }
  };

  const startExport = async (
    tabId: string,
    definitionId: string,
    parameters: Record<string, unknown>,
    presentation: ExportPresentation,
  ) => {
    resetTab(tabId); // one active export per tab
    const id = crypto.randomUUID();
    runs.value.push({
      id,
      tabId,
      definitionId,
      presentation,
      parameters: { ...parameters },
      runId: null,
      stage: "queued",
      rows: 0,
      status: "starting",
      error: null,
    });
    try {
      const res = await trpc.dataroom.createExport.mutate({
        definitionId,
        parameters,
        presentation,
        // Render the export in the language the user is reading the report in.
        locale: locale.value,
      });
      patch(id, { runId: res.runId, status: "running" });
      poll(id);
    } catch (e) {
      patch(id, {
        status: "error",
        error: e instanceof Error ? e.message : "export_failed",
      });
    }
  };

  /** Fetch a completed run's artifact and save it in the browser. */
  const download = async (id: string) => {
    const run = runs.value.find((r) => r.id === id);
    if (!run?.runId) return;
    try {
      const dl = await trpc.dataroom.getDownload.query({ runId: run.runId });
      triggerBrowserDownload(
        base64ToBlob(dl.base64, dl.contentType),
        dl.filename,
      );
    } catch (e) {
      patch(id, {
        status: "error",
        error: e instanceof Error ? e.message : "export_failed",
      });
    }
  };

  const runForTab = (tabId: string): ExportRun | null => {
    const matchingRuns = runs.value.filter((r) => r.tabId === tabId);
    return matchingRuns[matchingRuns.length - 1] ?? null;
  };

  const activeCount = computed(
    () =>
      runs.value.filter(
        (r) => r.status === "starting" || r.status === "running",
      ).length,
  );

  onBeforeUnmount(() => {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
  });

  return { runs, startExport, resetTab, download, runForTab, activeCount };
}
